import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { apiClient } from '../services/api';
import { Instance } from '../types/api';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

interface CollectionInfoModalProps {
  instanceId: number;
  onClose: () => void;
}

export const CollectionInfoModal: React.FC<CollectionInfoModalProps> = ({ instanceId, onClose }) => {
  useBodyScrollLock();
  const [instance, setInstance] = useState<Instance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creatorName, setCreatorName] = useState<string | null>(null);
  const [creatorLoading, setCreatorLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchInstance = async () => {
      try {
        setLoading(true);
        setError(null);
        setCreatorName(null);
        const response = await apiClient.getInstance(instanceId);
        if (!cancelled) {
          setInstance(response.instance);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load collection details');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    fetchInstance();
    return () => {
      cancelled = true;
    };
  }, [instanceId]);

  useEffect(() => {
    if (!instance) return;
    let cancelled = false;
    const fetchCreator = async () => {
      setCreatorLoading(true);
      setCreatorName(null);
      try {
        const user = await apiClient.getUser(instance.created_by);
        if (!cancelled) {
          setCreatorName([user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown user');
        }
      } catch {
        if (!cancelled) {
          setCreatorName('Unknown user');
        }
      } finally {
        if (!cancelled) {
          setCreatorLoading(false);
        }
      }
    };
    fetchCreator();
    return () => {
      cancelled = true;
    };
  }, [instance]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">Collection Info</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : instance ? (
          <div className="space-y-4">
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</dt>
              <dd className="mt-1 text-base text-gray-900">{instance.name}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</dt>
              <dd className="mt-1 text-sm text-gray-700">
                {instance.description || '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created by</dt>
              <dd className="mt-1 text-sm text-gray-700">
                {creatorLoading ? (
                  <span className="inline-flex items-center gap-1">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Loading…
                  </span>
                ) : (
                  creatorName ?? '—'
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created at</dt>
              <dd className="mt-1 text-sm text-gray-700">
                {new Date(instance.created_at).toLocaleString()}
              </dd>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
